import { useMemo } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { BASE_ROUTE } from '../../constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCatalogEntitiesDetail } from '../../hooks/useCatalogEntitiesDetail';
import {
  EmptyResultView,
  MikeDroppedImage,
  useClearEntityFilters,
} from '@mb.io/core-components';

export const EmptyResultViewWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearAllFilters } = useClearEntityFilters();
  const { totalItems = 0, starredEntities } = useCatalogEntitiesDetail();

  const isOnFavouritesPage = useMemo(
    () => location.pathname === `${BASE_ROUTE}/favourites`,
    [location.pathname],
  );
  const hasNoResults = useMemo(() => totalItems === 0, [totalItems]);
  const hasNoFavourites = useMemo(
    () => isOnFavouritesPage && !starredEntities.size,
    [isOnFavouritesPage, starredEntities],
  );

  const renderEmptyResult = () => (
    <EmptyResultView
      title="No results"
      message="Your criteria didn't return any results."
      suggestion="Please try a different combination of filters and search or"
      actionsChild={
        <Button
          sx={{
            borderRadius: '2px',
          }}
          variant="contained"
          color="primary"
          onClick={() => clearAllFilters()}
          data-testid="empty-result-clear-button"
        >
          Clear filters
        </Button>
      }
      imageChild={
        <Box
          sx={{
            height: 'auto',
            alignSelf: 'flex-end',
            marginTop: '2rem',
          }}
        >
          <MikeDroppedImage
            sx={{
              height: '320px',
              width: '445px',
              marginTop: {
                md: '-32px',
                lg: '-56px',
                xl: '-96px',
              },
            }}
            data-testid="empty-result-mike-dropped-image"
          />
        </Box>
      }
    />
  );

  const renderEmptyResultWhenNoFavourites = () => (
    <EmptyResultView
      title="No results"
      message="No favourites to display."
      actionsChild={
        <div>
          <Button
            sx={{
              borderRadius: '2px',
            }}
            variant="contained"
            color="primary"
            onClick={() => {
              navigate(`${BASE_ROUTE}/all`);
            }}
            data-testid="empty-result-explore-button"
          >
            Explore Catalog
          </Button>
        </div>
      }
    />
  );

  if (hasNoFavourites) {
    return renderEmptyResultWhenNoFavourites();
  }

  if (isOnFavouritesPage && hasNoResults) {
    return renderEmptyResult();
  }

  if (!isOnFavouritesPage && hasNoResults) {
    return renderEmptyResult();
  }

  return null;
};
